import React from 'react';
import './RichEditor.css';

// contentEditable leaves residue (e.g. "<br>", "<p><br></p>", "&nbsp;") even
// after the user clears all visible text. Treat those as empty so saves actually
// blank the field instead of persisting invisible markup.
function isHtmlBlank(html) {
  if (!html) return true;
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export default function RichEditor({ value, onChange, placeholder, minHeight = 120, disabled = false }) {
  const editorRef = React.useRef(null);
  const isInternalChange = React.useRef(false);

  // Sync external value → DOM only when it differs (avoid cursor jump)
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el || isInternalChange.current) return;
    if (el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  function handleInput() {
    isInternalChange.current = true;
    const html = editorRef.current.innerHTML;
    onChange(isHtmlBlank(html) ? '' : html);
    // Reset flag after React re-render
    requestAnimationFrame(() => { isInternalChange.current = false; });
  }

  function exec(cmd, arg = null) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  }

  function isActive(cmd) {
    try { return document.queryCommandState(cmd); } catch { return false; }
  }

  const tools = [
    { cmd: 'bold',          icon: <strong>B</strong>,   title: 'Bold (Ctrl+B)'      },
    { cmd: 'italic',        icon: <em>I</em>,           title: 'Italic (Ctrl+I)'    },
    { cmd: 'underline',     icon: <u>U</u>,             title: 'Underline (Ctrl+U)' },
    { cmd: 'strikeThrough', icon: <s>S</s>,             title: 'Strikethrough'      },
    { sep: true },
    { cmd: 'insertUnorderedList', icon: '≡', title: 'Bullet list' },
    { cmd: 'insertOrderedList',   icon: '1.', title: 'Numbered list' },
    { sep: true },
    { action: () => exec('formatBlock', '<h3>'), icon: 'H', title: 'Heading'         },
    { action: () => exec('formatBlock', '<p>'),  icon: '¶', title: 'Paragraph'       },
    { sep: true },
    { action: () => {
        const url = window.prompt('Enter URL:');
        if (url) exec('createLink', url);
      }, icon: '🔗', title: 'Insert link' },
    { action: () => exec('unlink'), icon: '✂', title: 'Remove link' },
    { sep: true },
    { action: () => exec('removeFormat'), icon: '✕', title: 'Clear formatting' },
  ];

  return (
    <div className={`re-wrap ${disabled ? 're-disabled' : ''}`}>
      {!disabled && (
        <div className="re-toolbar">
          {tools.map((tool, i) =>
            tool.sep ? (
              <span key={i} className="re-sep"/>
            ) : (
              <button
                key={i}
                type="button"
                title={tool.title}
                className={`re-btn ${!tool.action && isActive(tool.cmd) ? 'active' : ''}`}
                onMouseDown={e => {
                  e.preventDefault(); // keep focus in editor
                  tool.action ? tool.action() : exec(tool.cmd);
                }}
              >
                {tool.icon}
              </button>
            )
          )}
        </div>
      )}
      <div
        ref={editorRef}
        className="re-body"
        contentEditable={!disabled}
        suppressContentEditableWarning
        style={{ minHeight }}
        onInput={handleInput}
        data-placeholder={placeholder || 'Write here…'}
      />
    </div>
  );
}
